import { 
  doc, 
  collection, 
  getDocs, 
  getDoc, 
  setDoc, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  increment, 
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { cachedFirebase } from './cachedFirebase';
import { createAppointment as createAppointmentInFirebase } from './appointments';

/**
 * Normaliza um número de telefone removendo caracteres não numéricos
 * @param {string} phone - Número de telefone
 * @returns {string|null} - Número normalizado ou null se inválido
 */
export const normalizePhone = (phone) => {
  if (!phone) return null;
  return phone.replace(/\D/g, '');
};

/**
 * Busca um cliente pelo telefone ou email
 * @param {string} phone - Número de telefone (opcional)
 * @param {string} email - Email (opcional)
 * @returns {Promise<object|null>} - Cliente encontrado ou null
 */
export const findClientByPhoneOrEmail = async (phone, email) => {
  if (!phone && !email) return null;
  
  let platformClientId = null;
  
  try {
    // Tentar encontrar por telefone
    if (phone) {
      const normalizedPhone = normalizePhone(phone);
      const phoneLookupRef = doc(collection(db, "client_lookups", "by_phone", "phones"), normalizedPhone);
      const phoneLookupSnap = await getDoc(phoneLookupRef);
      
      if (phoneLookupSnap.exists()) {
        platformClientId = phoneLookupSnap.data().platformClientId;
      }
    }
    
    // Se não encontrou por telefone, tentar por email
    if (!platformClientId && email) {
      const normalizedEmail = email.toLowerCase();
      const emailLookupRef = doc(collection(db, "client_lookups", "by_email", "emails"), normalizedEmail);
      const emailLookupSnap = await getDoc(emailLookupRef);
      
      if (emailLookupSnap.exists()) {
        platformClientId = emailLookupSnap.data().platformClientId;
      }
    }
    
    // Se encontrou um ID, buscar o cliente da plataforma
    if (platformClientId) {
      const clientRef = doc(db, "platform_clients", platformClientId);
      const clientSnap = await getDoc(clientRef);
      
      if (clientSnap.exists()) {
        // Buscar identidades do cliente
        const identitiesRef = collection(db, "platform_clients", platformClientId, "identities");
        const identitiesSnap = await getDocs(identitiesRef);
        
        const identities = identitiesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Buscar estatísticas por setor
        const sectorsRef = collection(db, "platform_clients", platformClientId, "sectors");
        const sectorsSnap = await getDocs(sectorsRef);
        
        const sectors = sectorsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        return {
          id: platformClientId,
          ...clientSnap.data(),
          identities,
          sectors
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Erro ao buscar cliente:', error);
    throw error;
  }
};

/**
 * Busca um cliente específico de um negócio
 * @param {string} businessId - ID do negócio
 * @param {string} phone - Número de telefone (opcional)
 * @param {string} email - Email (opcional)
 * @returns {Promise<object|null>} - Cliente do negócio encontrado ou null
 */
export const findBusinessClientByPhoneOrEmail = async (businessId, phone, email) => {
  if (!businessId || (!phone && !email)) return null;
  
  try {
    const clientsRef = collection(db, "salons", businessId, "clients");
    let clientQuery;
    
    if (phone) {
      const normalizedPhone = normalizePhone(phone);
      clientQuery = query(
        clientsRef,
        where("celularNormalizado", "==", normalizedPhone)
      );
    } else {
      const normalizedEmail = email.toLowerCase();
      clientQuery = query(
        clientsRef,
        where("emailNormalizado", "==", normalizedEmail)
      );
    }
    
    const clientsSnapshot = await getDocs(clientQuery);
    
    if (!clientsSnapshot.empty) {
      const clientDoc = clientsSnapshot.docs[0];
      return {
        id: clientDoc.id,
        ...clientDoc.data()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Erro ao buscar cliente do negócio:', error);
    throw error;
  }
};

/**
 * Busca todos os clientes de um negócio com suporte a cache
 * @param {string} businessId - ID do negócio
 * @param {object} options - Opções de consulta e cache
 * @returns {Promise<Array>} - Lista de clientes
 */
export const getBusinessClients = async (businessId, options = {}) => {
  if (!businessId) throw new Error('ID do negócio é obrigatório');
  
  const { 
    search = '',
    orderField = 'nome',
    orderDirection = 'asc',
    limit = 50,
    forceRefresh = false 
  } = options;
  
  try {
    const collectionPath = `salons/${businessId}/clients`;
    
    // Definir ordenação
    const orderByFields = [
      { field: orderField, direction: orderDirection }
    ];
    
    // Definir condições de filtragem se houver termo de busca
    let whereConditions = [];
    
    if (search) {
      // Criar consulta com OR implícito (union de resultados)
      const normalizedSearch = search.toLowerCase();
      const normalizedPhone = normalizePhone(search);
      
      try {
        // Primeiro, vamos carregar todos os clientes e depois filtrar pelo termo de busca
        // Este método é mais eficiente para busca por substring em Firestore
        const allClients = await cachedFirebase.getCollection(
          collectionPath,
          {
            orderByFields,
            limitCount: 100 // Limitamos a 100 clientes para não sobrecarregar
          },
          { forceRefresh }
        );
        
        // Filtrar clientes que contenham o termo de busca em qualquer parte do nome
        const filteredByName = allClients.filter(client => 
          client.nome && client.nome.toLowerCase().includes(normalizedSearch)
        );
        
        // Se tiver aparência de email, filtrar por email também
        const filteredByEmail = normalizedSearch.includes('@') 
          ? allClients.filter(client => 
              client.email && client.email.toLowerCase().includes(normalizedSearch)
            )
          : [];
        
        // Se tiver números, filtrar por telefone também
        const filteredByPhone = normalizedPhone 
          ? allClients.filter(client => 
              client.celular && client.celular.includes(normalizedPhone)
            ) 
          : [];
        
        // Mesclar resultados removendo duplicatas (por ID)
        const mergedResults = [];
        const seenIds = new Set();
        
        [...filteredByName, ...filteredByEmail, ...filteredByPhone].forEach(client => {
          if (!seenIds.has(client.id)) {
            seenIds.add(client.id);
            mergedResults.push(client);
          }
        });
        
        // Ordenar resultados mesclados
        mergedResults.sort((a, b) => {
          const aValue = a[orderField] || '';
          const bValue = b[orderField] || '';
          
          if (orderDirection === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        });
        
        return mergedResults.slice(0, limit);
      } catch (error) {
        console.error('Erro ao buscar e filtrar clientes:', error);
        
        // Fallback para o método original caso ocorra algum erro
        const queries = await Promise.all([
          // Busca por nome (prefixo)
          cachedFirebase.getCollection(
            collectionPath,
            {
              whereConditions: [
                { field: 'nome', operator: '>=', value: normalizedSearch },
                { field: 'nome', operator: '<=', value: normalizedSearch + '\uf8ff' }
              ],
              orderByFields,
              limitCount: limit
            },
            { forceRefresh }
          ),
          
          // Busca por email se o termo parece um email
          normalizedSearch.includes('@') ? 
            cachedFirebase.getCollection(
              collectionPath,
              {
                whereConditions: [
                  { field: 'emailNormalizado', operator: '>=', value: normalizedSearch },
                  { field: 'emailNormalizado', operator: '<=', value: normalizedSearch + '\uf8ff' }
                ],
                orderByFields,
                limitCount: limit
              },
              { forceRefresh }
            ) : [],
          
          // Busca por telefone se o termo tem números
          normalizedPhone ? 
            cachedFirebase.getCollection(
              collectionPath,
              {
                whereConditions: [
                  { field: 'celularNormalizado', operator: '>=', value: normalizedPhone },
                  { field: 'celularNormalizado', operator: '<=', value: normalizedPhone + '\uf8ff' }
                ],
                orderByFields,
                limitCount: limit
              },
              { forceRefresh }
            ) : []
        ]);
        
        // Mesclar resultados removendo duplicatas (por ID)
        const mergedResults = [];
        const seenIds = new Set();
        
        queries.flat().forEach(client => {
          if (!seenIds.has(client.id)) {
            seenIds.add(client.id);
            mergedResults.push(client);
          }
        });
        
        // Ordenar resultados mesclados
        mergedResults.sort((a, b) => {
          const aValue = a[orderField] || '';
          const bValue = b[orderField] || '';
          
          if (orderDirection === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        });
        
        return mergedResults.slice(0, limit);
      }
    } else {
      // Sem filtro de busca, retornar todos ordenados
      return cachedFirebase.getCollection(
        collectionPath,
        {
          orderByFields,
          limitCount: limit
        },
        { forceRefresh }
      );
    }
  } catch (error) {
    console.error('Erro ao buscar clientes do negócio:', error);
    throw error;
  }
};

/**
 * Cria ou atualiza um cliente e faz o agendamento usando a função centralizada
 * @param {string} businessId - ID do negócio
 * @param {object} appointmentData - Dados do agendamento, incluindo cliente
 * @returns {Promise<object>} - Resultado da operação
 */
export const createOrUpdateClientAndBook = async (businessId, appointmentData) => {
  if (!businessId) throw new Error('ID do negócio é obrigatório');
  
  try {
    // Verificar o formato dos dados recebidos e normalizar
    let clienteData = {};
    let appointmentDataFormatted = { ...appointmentData };
    
    // Caso 1: cliente é uma string (nome do cliente)
    if (typeof appointmentData.cliente === 'string') {
      clienteData = {
        nome: appointmentData.cliente,
        celular: appointmentData.whatsapp,
        email: appointmentData.email || ""
      };
    } 
    // Caso 2: cliente é um objeto (formato correto)
    else if (typeof appointmentData.cliente === 'object') {
      clienteData = appointmentData.cliente;
    }
    else {
      throw new Error('Dados do cliente inválidos');
    }
    
    // Normalizar identificadores
    const normalizedPhone = normalizePhone(clienteData.celular || appointmentData.whatsapp);
    const normalizedEmail = (clienteData.email || appointmentData.email || "").toLowerCase();
    
    // Validar que pelo menos um identificador foi fornecido
    if (!normalizedPhone && !normalizedEmail) {
      throw new Error('Pelo menos um contato (celular ou email) é obrigatório');
    }
    
    // 1. Buscar cliente no contexto específico do negócio
    let businessClientId;
    let businessClientData;
    
    const clientsRef = collection(db, "salons", businessId, "clients");
    let clientQuery;
    
    if (normalizedPhone) {
      clientQuery = query(
        clientsRef,
        where("celularNormalizado", "==", normalizedPhone)
      );
    } else {
      clientQuery = query(
        clientsRef,
        where("emailNormalizado", "==", normalizedEmail)
      );
    }
    
    const clientsSnapshot = await getDocs(clientQuery);
    
    if (!clientsSnapshot.empty) {
      // Cliente já existe neste negócio - usar o ID existente
      const clientDoc = clientsSnapshot.docs[0];
      businessClientId = clientDoc.id;
      businessClientData = clientDoc.data();
      
      // Atualizar dados do cliente se necessário
      if (clienteData.nome && clienteData.nome !== businessClientData.nome) {
        await updateDoc(doc(db, "salons", businessId, "clients", businessClientId), {
          nome: clienteData.nome,
          updatedAt: serverTimestamp()
        });
        
        // Invalidar cache
        cachedFirebase.invalidateCache(`salons/${businessId}/clients/${businessClientId}`);
      }
    } else {
      // Cliente não existe neste negócio - criar novo
      // Primeiro verificar se existe na plataforma global
      let platformClientId;
      
      if (normalizedPhone) {
        const phoneLookupRef = doc(collection(db, "client_lookups", "by_phone", "phones"), normalizedPhone);
        const phoneLookupSnap = await getDoc(phoneLookupRef);
        
        if (phoneLookupSnap.exists()) {
          platformClientId = phoneLookupSnap.data().platformClientId;
        }
      }
      
      if (!platformClientId && normalizedEmail) {
        const emailLookupRef = doc(collection(db, "client_lookups", "by_email", "emails"), normalizedEmail);
        const emailLookupSnap = await getDoc(emailLookupRef);
        
        if (emailLookupSnap.exists()) {
          platformClientId = emailLookupSnap.data().platformClientId;
        }
      }
      
      // Se não encontrou, criar novo client na plataforma
      if (!platformClientId) {
        const platformClientRef = doc(collection(db, "platform_clients"));
        platformClientId = platformClientRef.id;
        
        const batch = writeBatch(db);
        
        // Inserir dados básicos no cliente da plataforma
        batch.set(platformClientRef, {
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Criar identidades e lookups
        if (normalizedPhone) {
          // Adicionar identidade de telefone
          const phoneIdentityRef = doc(collection(db, "platform_clients", platformClientId, "identities"));
          batch.set(phoneIdentityRef, {
            type: "phone",
            value: clienteData.celular,
            normalizedValue: normalizedPhone,
            createdAt: serverTimestamp()
          });
          
          // Criar lookup de telefone
          batch.set(doc(collection(db, "client_lookups", "by_phone", "phones"), normalizedPhone), {
            platformClientId
          });
        }
        
        if (normalizedEmail) {
          // Adicionar identidade de email
          const emailIdentityRef = doc(collection(db, "platform_clients", platformClientId, "identities"));
          batch.set(emailIdentityRef, {
            type: "email",
            value: clienteData.email,
            normalizedValue: normalizedEmail,
            createdAt: serverTimestamp()
          });
          
          // Criar lookup de email
          batch.set(doc(collection(db, "client_lookups", "by_email", "emails"), normalizedEmail), {
            platformClientId
          });
        }
        
        // Commit do batch
        await batch.commit();
        
        // Obter tipo de negócio para criar estatísticas de setor
        const businessRef = doc(db, "salons", businessId);
        const businessSnap = await getDoc(businessRef);
        const businessType = businessSnap.data()?.businessType || "outros";
        
        // Criar estatísticas de setor
        await setDoc(doc(db, "platform_clients", platformClientId, "sectors", businessType), {
          sectorType: businessType,
          visitCount: 0,
          lastVisitDate: null,
          businessAppearances: [{
            businessId,
            visitCount: 0
          }]
        });
      }
      
      // Criar cliente no negócio
      const businessClientRef = doc(collection(db, "salons", businessId, "clients"));
      businessClientId = businessClientRef.id;
      
      businessClientData = {
        nome: clienteData.nome,
        celular: clienteData.celular,
        celularNormalizado: normalizedPhone,
        email: clienteData.email,
        emailNormalizado: normalizedEmail,
        platformClientId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(businessClientRef, businessClientData);
      
      // Invalidar cache da coleção de clientes
      cachedFirebase.invalidateCache(`salons/${businessId}/clients`);
    }
    
    // 2. Criar agendamento usando a função centralizada
    // Preparar dados para envio ao createAppointment
    const formattedAppointmentData = {
      ...appointmentDataFormatted,
      clienteId: businessClientId,
      cliente: clienteData.nome,
      whatsapp: normalizedPhone,
      email: normalizedEmail || ""
    };
    
    // Usar a função centralizada
    const appointmentResult = await createAppointmentInFirebase(businessId, formattedAppointmentData);
    
    // 3. Atualizar estatísticas de cliente na plataforma global
    if (businessClientData.platformClientId) {
      // Obter tipo de negócio
      const businessRef = doc(db, "salons", businessId);
      const businessSnap = await getDoc(businessRef);
      const businessType = businessSnap.data()?.businessType || "outros";
      
      // Atualizar estatísticas de setor
      const sectorRef = doc(db, "platform_clients", businessClientData.platformClientId, "sectors", businessType);
      const sectorSnap = await getDoc(sectorRef);
      
      if (sectorSnap.exists()) {
        const sectorData = sectorSnap.data();
        const appearances = sectorData.businessAppearances || [];
        const businessIndex = appearances.findIndex(a => a.businessId === businessId);
        
        if (businessIndex >= 0) {
          appearances[businessIndex].visitCount++;
        } else {
          appearances.push({
            businessId,
            visitCount: 1
          });
        }
        
        await updateDoc(sectorRef, {
          visitCount: increment(1),
          lastVisitDate: serverTimestamp(),
          businessAppearances: appearances
        });
      } else {
        await setDoc(sectorRef, {
          sectorType: businessType,
          visitCount: 1,
          lastVisitDate: serverTimestamp(),
          businessAppearances: [{
            businessId,
            visitCount: 1
          }]
        });
      }
      
      // Atualizar stats gerais
      const statsRef = doc(collection(db, "platform_clients", businessClientData.platformClientId, "stats"), "summary");
      await setDoc(statsRef, {
        totalAppointments: increment(1),
        lastAppointmentDate: serverTimestamp()
      }, { merge: true });
    }
    
    return {
      success: true,
      appointmentId: appointmentResult.id,
      clientId: businessClientId
    };
  } catch (error) {
    console.error('Erro ao criar cliente e agendamento:', error);
    return {
      success: false,
      error: error.message || 'Erro desconhecido ao criar cliente e agendamento'
    };
  }
};

/**
 * Obtém os dados de um cliente específico com suporte a cache
 * @param {string} businessId - ID do negócio
 * @param {string} clientId - ID do cliente no negócio
 * @param {boolean} includePlatformData - Se deve incluir dados da plataforma
 * @returns {Promise<object|null>} - Dados do cliente
 */
export const getClientById = async (businessId, clientId, includePlatformData = false) => {
  if (!businessId) throw new Error('ID do negócio é obrigatório');
  if (!clientId) throw new Error('ID do cliente é obrigatório');
  
  try {
    // Buscar dados do cliente no negócio
    const clientPath = `salons/${businessId}/clients/${clientId}`;
    const clientData = await cachedFirebase.getDocument(clientPath);
    
    if (!clientData) return null;
    
    // Se não precisa dos dados da plataforma, retornar apenas dados do negócio
    if (!includePlatformData || !clientData.platformClientId) return clientData;
    
    // Buscar dados adicionais da plataforma
    const platformClientPath = `platform_clients/${clientData.platformClientId}`;
    const platformClientData = await cachedFirebase.getDocument(platformClientPath);
    
    if (!platformClientData) return clientData;
    
    // Buscar identidades
    const identitiesPath = `platform_clients/${clientData.platformClientId}/identities`;
    const identities = await cachedFirebase.getCollection(identitiesPath);
    
    // Buscar setores
    const sectorsPath = `platform_clients/${clientData.platformClientId}/sectors`;
    const sectors = await cachedFirebase.getCollection(sectorsPath);
    
    return {
      ...clientData,
      platformData: {
        ...platformClientData,
        identities,
        sectors
      }
    };
  } catch (error) {
    console.error('Erro ao buscar cliente por ID:', error);
    throw error;
  }
};

/**
 * Obtém uma visão geral de clientes globais da plataforma por setor
 * @param {string} sectorType - Tipo de setor (opcional)
 * @param {object} options - Opções de consulta
 * @returns {Promise<object>} - Estatísticas de clientes por setor
 */
export const getPlatformClientStats = async (sectorType = null, options = {}) => {
  const { limit = 1000, forceRefresh = false } = options;
  
  try {
    // Consultar setores se sectorType for fornecido
    if (sectorType) {
      // Buscar todos os documentos de setor específico em platform_clients
      // Esta operação usará uma consulta composta que pode ser cara
      // É melhor usar funções no servidor para esta operação
      
      // Implementação exemplo de consulta, mas o ideal seria implementar um Cloud Function
      const sectorsQuery = query(
        collection(db, "platform_clients"),
        where(`sectors.${sectorType}`, "!=", null),
        limit
      );
      
      // Esta consulta seria muito custosa e provavelmente não funcionaria bem
      // Recomendamos criar um Cloud Function específico para esta análise
      
      return {
        message: "Esta consulta é melhor implementada como Cloud Function",
        recommendation: "Implemente um serviço de agregação de dados para análises globais"
      };
    } else {
      // Estatísticas gerais são melhores implementadas como dados pré-agregados
      // em uma coleção de estatísticas, atualizadas por Triggers do Firestore
      
      return {
        message: "Estatísticas globais devem usar dados pré-agregados",
        recommendation: "Implemente um Cloud Function para agregação periódica de estatísticas"
      };
    }
  } catch (error) {
    console.error('Erro ao buscar estatísticas de clientes:', error);
    throw error;
  }
};

/**
 * Exporta funções e utilitários para uso fora do módulo
 */
export default {
  normalizePhone,
  findClientByPhoneOrEmail,
  findBusinessClientByPhoneOrEmail,
  getBusinessClients,
  createOrUpdateClientAndBook,
  getClientById,
  getPlatformClientStats
}; 